/**
 * Decision Room outcome control — Open / Won / Lost (2026-09-07).
 *
 * The room's status had no seller-facing control at all: Archive was
 * the only lifecycle action, and won/lost were only ever set by the
 * e-sign completed-envelope hook. This gives the seller the call.
 *
 * Marking a room LOST captures WHY, from the company's shared
 * loss-reason taxonomy (backend Phase 0). The picker is required when
 * the `loss_reasons` flag is on for the workspace; when it's off the
 * PATCH goes straight through. Either way, a 400 `loss_reason_required`
 * from the server opens the picker — the flag read here can be stale
 * for up to a minute after a flip, and the server is the authority.
 *
 * Seller-side surface: --room-* tokens, never hardcoded neutrals.
 */

import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/apiRequest';

const OUTCOMES = [
  { id: 'open', label: 'Open' },
  { id: 'won',  label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

export default function RoomStatusControl({ spaceId, authToken, currentStatus, uiFlags = {}, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const isArchived = currentStatus === 'dormant';

  const patch = async (body) => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/api/buyer-spaces/${spaceId}`, { token: authToken, method: 'PATCH', body });
      setPickerOpen(false);
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      if (err.code === 'loss_reason_required') {
        setPickerOpen(true);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const choose = (next) => {
    if (busy || isArchived || next === currentStatus) return;
    if (next === 'won') {
      if (!window.confirm('Mark this Decision Room as WON? The close date is recorded now.')) return;
      patch({ status: 'won' });
      return;
    }
    if (next === 'lost') {
      if (uiFlags.loss_reasons) { setPickerOpen(true); return; }
      if (!window.confirm('Mark this Decision Room as LOST?')) return;
      patch({ status: 'lost' });
      return;
    }
    patch({ status: 'open' });
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--room-text-subtle, #94a3b8)', marginBottom: 8 }}>
        Outcome
      </div>
      <div role="group" aria-label="Decision Room outcome" style={{ display: 'flex', gap: 4, background: 'var(--room-surface, #f8fafc)', padding: 3, borderRadius: 10, border: '1px solid var(--room-border-soft, #f1f5f9)' }}>
        {OUTCOMES.map((o) => {
          const active = currentStatus === o.id;
          const tone = o.id === 'won' ? '#16a34a' : o.id === 'lost' ? '#dc2626' : '#7c3aed';
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={active}
              disabled={busy || isArchived}
              onClick={() => choose(o.id)}
              style={{
                flex: 1, padding: '6px 0', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                border: active ? `1px solid ${tone}` : '1px solid transparent',
                background: active ? 'var(--room-card, #fff)' : 'transparent',
                color: active ? tone : 'var(--room-text-muted, #64748b)',
                cursor: busy || isArchived ? 'default' : 'pointer',
                opacity: isArchived ? 0.5 : 1,
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {isArchived && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)' }}>
          Archived — reactivate below to change the outcome.
        </div>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>{error}</div>
      )}
      {pickerOpen && (
        <LossReasonDialog
          authToken={authToken}
          busy={busy}
          onCancel={() => setPickerOpen(false)}
          onConfirm={({ lossReasonId, lossNotes }) => patch({ status: 'lost', lossReasonId, lossNotes })}
        />
      )}
    </div>
  );
}

/**
 * Modal picker for the loss reason. Loads the company taxonomy, lets
 * the seller add a missing reason inline (shared vocabulary: it shows
 * up for everyone and in win/loss survey fields from then on), and
 * takes optional free-text notes.
 */
export function LossReasonDialog({ authToken, busy, onCancel, onConfirm }) {
  const [reasons, setReasons] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [reasonId, setReasonId] = useState('');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiRequest('/api/loss-reasons', { token: authToken });
        if (!cancelled) setReasons(r.lossReasons || []);
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [authToken]);

  const addReason = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setAddBusy(true);
    setAddError(null);
    try {
      const r = await apiRequest('/api/loss-reasons', { token: authToken, method: 'POST', body: { label } });
      const reason = r.lossReason;
      setReasons((prev) => {
        const list = prev || [];
        return list.some((x) => x.id === reason.id) ? list : [...list, reason];
      });
      setReasonId(reason.id);
      setNewLabel('');
      setAdding(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddBusy(false);
    }
  };

  const field = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
    border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)',
    color: 'var(--room-text-strong, #0f172a)', fontSize: 14, fontFamily: 'inherit',
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="loss-reason-title"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)', borderRadius: 16, border: '1px solid var(--room-border, #e2e8f0)', padding: 22, boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}>
        <h2 id="loss-reason-title" style={{ margin: '0 0 4px', fontSize: 18 }}>Why was this deal lost?</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
          Reasons are shared across your workspace, so win/loss surveys and your own calls add up to one picture.
        </p>

        {loadError && <div role="alert" style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{loadError}</div>}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }} htmlFor="loss-reason-select">Reason</label>
        <select id="loss-reason-select" value={reasonId} onChange={(e) => setReasonId(e.target.value)} disabled={!reasons} style={field}>
          <option value="">{reasons ? 'Pick a reason…' : 'Loading…'}</option>
          {(reasons || []).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>

        {adding ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              type="text" value={newLabel} maxLength={120} placeholder="New reason, e.g. Chose a competitor"
              onChange={(e) => setNewLabel(e.target.value)} style={field} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReason(); } }}
            />
            <button type="button" onClick={addReason} disabled={addBusy || !newLabel.trim()} style={ghostBtn}>{addBusy ? 'Adding…' : 'Add'}</button>
          </div>
        ) : (
          <button type="button" onClick={() => setAdding(true)} style={{ ...linkBtn, marginTop: 8 }}>+ Add a reason</button>
        )}
        {addError && <div role="alert" style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{addError}</div>}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }} htmlFor="loss-notes">Notes <span style={{ fontWeight: 400, color: 'var(--room-text-subtle, #94a3b8)' }}>(optional)</span></label>
        <textarea id="loss-notes" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...field, resize: 'vertical' }} placeholder="Anything the team should know for next time" />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onCancel} disabled={busy} style={ghostBtn}>Cancel</button>
          <button
            type="button"
            disabled={busy || !reasonId}
            onClick={() => onConfirm({ lossReasonId: reasonId, lossNotes: notes.trim() || null })}
            style={{ ...ghostBtn, background: '#dc2626', borderColor: '#dc2626', color: '#fff', opacity: busy || !reasonId ? 0.6 : 1 }}
          >
            {busy ? 'Saving…' : 'Mark as lost'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ghostBtn = {
  padding: '8px 14px', borderRadius: 10, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)',
};
const linkBtn = {
  background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#7c3aed', cursor: 'pointer',
};
